// Geometry the WADO loader gets wrong or misses, answered before its own providers:
//
// * Enhanced multi-frame (fast mode, wadors frames): the loader drops the Shared Functional Groups
//   (framegeometry.ts), so each frame's plane and VOI are rebuilt from the instance's own functional groups.
// * Ultrasound: the loader reads only Pixel Spacing (0028,0030), which ultrasound rarely has; a calibrated
//   ultrasound region supplies it (usregions.ts), so every measurement tool reads in mm.
// JPEG-frame images (parity multi-frame) get the same fallbacks in jpegframes.ts.

import type { DicomJson } from './dicomweb';
import type { DicomParserDataSet } from './globals';
import { frameVoi, framePlane, type FramePlane } from './framegeometry';
import { resolveSpacing } from './spacing';
import { calibrationFrom, regionFrom, regionsFromJson, US_REGIONS_TAG, type RegionVr, type UsRegion } from './usregions';

/** Above the WADO loader's own providers (registered at the default priority 0). */
const PRIORITY = 100;

interface LoaderPlane {
  rowPixelSpacing?: number | null;
  columnPixelSpacing?: number | null;
}

function regionsFromDataSet(dataSet: DicomParserDataSet | undefined): UsRegion[] {
  const items = dataSet?.elements[`x${US_REGIONS_TAG.toLowerCase()}`]?.items ?? [];
  return items.flatMap((item) => {
    const ds = item.dataSet;
    if (!ds) return [];
    const read = (tag: string, vr: RegionVr): number | undefined => {
      const key = `x${tag.toLowerCase()}`;
      if (!ds.elements[key]) return undefined;
      if (vr === 'FD') return ds.double(key);
      return vr === 'UL' ? ds.uint32(key) : ds.uint16(key);
    };
    return [regionFrom(read)];
  });
}

interface RawSpacing {
  pixelSpacing?: unknown;
  imagerPixelSpacing?: unknown;
  ermf?: unknown;
}

const SPACING_TAGS = { pixelSpacing: '00280030', imagerPixelSpacing: '00181164', ermf: '00181114' } as const;

function rawSpacingFromJson(json: DicomJson | undefined): RawSpacing {
  return {
    pixelSpacing: json?.[SPACING_TAGS.pixelSpacing]?.Value,
    imagerPixelSpacing: json?.[SPACING_TAGS.imagerPixelSpacing]?.Value,
    ermf: json?.[SPACING_TAGS.ermf]?.Value,
  };
}

function rawSpacingFromDataSet(ds: DicomParserDataSet | undefined): RawSpacing {
  const read = (tag: string): string | undefined => ds?.string(`x${tag}`);
  return {
    pixelSpacing: read(SPACING_TAGS.pixelSpacing),
    imagerPixelSpacing: read(SPACING_TAGS.imagerPixelSpacing),
    ermf: read(SPACING_TAGS.ermf),
  };
}

function loaderPlaneAndRegions(imageId: string): { plane: LoaderPlane | undefined; regions: UsRegion[]; raw: RawSpacing } {
  const loader = window.cornerstoneWADOImageLoader;
  if (imageId.startsWith('wadors:')) {
    const json = loader.wadors.metaDataManager.get(imageId) as DicomJson | undefined;
    return {
      plane: loader.wadors.metaData.metaDataProvider('imagePlaneModule', imageId) as LoaderPlane | undefined,
      regions: regionsFromJson(json),
      raw: rawSpacingFromJson(json),
    };
  }
  if (imageId.startsWith('wadouri:')) {
    const { url } = loader.wadouri.parseImageId(imageId);
    const dataSet = loader.wadouri.dataSetCacheManager.get(url);
    return {
      plane: loader.wadouri.metaData.metaDataProvider('imagePlaneModule', imageId) as LoaderPlane | undefined,
      regions: regionsFromDataSet(dataSet),
      raw: rawSpacingFromDataSet(dataSet),
    };
  }
  return { plane: undefined, regions: [], raw: {} };
}

/** Enhanced multi-frame frames registered by the loader: imageId → (the instance's own metadata, 1-based frame). */
const enhancedFrames = new Map<string, { metadata: DicomJson; frame: number }>();

export function registerEnhancedFrame(imageId: string, metadata: DicomJson, frame: number): void {
  enhancedFrames.set(imageId, { metadata, frame });
}

function defined(plane: FramePlane): Partial<FramePlane> {
  return Object.fromEntries(Object.entries(plane).filter(([, v]) => v !== undefined)) as Partial<FramePlane>;
}

function providePlane(imageId: string): unknown {
  const { plane, regions, raw } = loaderPlaneAndRegions(imageId);
  const source = enhancedFrames.get(imageId);
  const enhanced = source ? framePlane(source.metadata, source.frame) : undefined;
  const merged = enhanced && plane ? { ...plane, ...defined(enhanced) } : plane;
  if (!merged) return undefined;
  if (merged.rowPixelSpacing && merged.columnPixelSpacing) return enhanced ? merged : undefined;
  // Pixel Spacing missing: take the detector spacing (Imager Pixel Spacing / magnification) before any US region.
  const resolved = resolveSpacing(raw);
  if (resolved) return { ...merged, rowPixelSpacing: resolved.row, columnPixelSpacing: resolved.column };
  const calibration = calibrationFrom(regions);
  if (!calibration) return enhanced ? merged : undefined; // nothing to add: the loader's answer stands
  return { ...merged, rowPixelSpacing: calibration.rowPixelSpacing, columnPixelSpacing: calibration.columnPixelSpacing };
}

function provideVoi(imageId: string): unknown {
  const source = enhancedFrames.get(imageId);
  return source ? frameVoi(source.metadata, source.frame) : undefined;
}

function provide(type: string, imageId: string): unknown {
  if (type === 'imagePlaneModule') return providePlane(imageId);
  if (type === 'voiLutModule') return provideVoi(imageId);
  return undefined;
}

let installed = false;

export function installGeometryProvider(): void {
  if (installed) return;
  installed = true;
  window.cornerstone.metaData.addProvider(provide, PRIORITY);
}
